import {
  IRelationships,
  Link,
  NotFoundIcon,
  useEllipsis,
  useEnvironment,
  worksQuery,
  WorkStatus,
} from '@diana/core';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';
import { Launch as LaunchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { RelatedWorkItemStyled } from './RelatedWork.style';
import { useTranslation } from 'react-i18next';
import { WORK_PAGE_NS } from '../../i18n';
import { routeUrls } from '../../../../app/Routing/RouteUrls';
import { useCallback } from 'react';

function RelatedWorkEllipsesText({ text }: { text: string }) {
  return useEllipsis({ text, sx: { m: 0, fontWeight: 500, width: '100%' } });
}

const RelatedWorkEmptyView = () => {
  const { t } = useTranslation(WORK_PAGE_NS);
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={2}
    >
      <NotFoundIcon width="100px" height="100px" />
      <Typography color="text.secondary">{t('relatedWork.empty')}</Typography>
    </Box>
  );
};

const RelatedWorkItem = ({
  workId,
  relationship,
  isMultipleFlows,
  showWorkStatus,
}: {
  workId: string;
  relationship: IRelationships;
  isMultipleFlows: boolean;
  showWorkStatus: boolean;
}) => {
  const { entityId, code, workName, name, entityType } = relationship;
  const { t } = useTranslation(WORK_PAGE_NS);
  const environment = useEnvironment();

  const renderIcon = useCallback(() => {
    return (
      <IconButton>
        <LaunchIcon />
      </IconButton>
    );
  }, []);

  const workUrl =
    entityType?.toLowerCase() === 'companyentity'
      ? routeUrls.thing.url({ environment, thingId: entityId }, null)
      : routeUrls.work.url({ environment, workId: entityId }, {});

  const query = worksQuery.getByRelationshipName({
    id: workId,
    relationshipName: relationship.name,
    format: 'compact',
  });

  const { data: work, isLoading } = useQuery({
    queryKey: query.key,
    queryFn: query.fn,
    refetchOnMount: false,
    enabled: showWorkStatus,
    meta: { persist: false },
    select: (data) => (data.id === entityId ? data : null),
  });

  return (
    <RelatedWorkItemStyled isMultipleFlows={isMultipleFlows}>
      <Link isExternal to={workUrl} renderIcon={renderIcon}>
        <Box>
          <RelatedWorkEllipsesText text={`${workName ?? ''} ${code ?? ''}`} />
          <Typography color="text.secondary" fontSize="12px">
            {t('relatedWork.relationship.type')}: {name}
          </Typography>
          {isLoading ? <Skeleton variant="text" width="50%" sx={{ marginBottom: 1 }} /> : null}
          {work && showWorkStatus ? (
            <WorkStatus
              work={work}
              sxBox={{
                py: 0,
                px: 1,
                minHeight: 'auto',
                display: 'inline-block',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            />
          ) : null}
        </Box>
      </Link>
    </RelatedWorkItemStyled>
  );
};

const RelatedWorkGroup = ({
  workId,
  flowName,
  relationships,
  isMultipleFlows,
  showWorkStatus,
}: {
  workId: string;
  flowName: string;
  relationships: IRelationships[];
  isMultipleFlows: boolean;
  showWorkStatus: boolean;
}) => {
  return (
    <Box>
      {isMultipleFlows ? (
        <Typography fontWeight={500} margin={'4px 0'}>
          {flowName}
        </Typography>
      ) : null}
      {relationships.map((relationship) => (
        <RelatedWorkItem
          workId={workId}
          relationship={relationship}
          isMultipleFlows={isMultipleFlows}
          key={relationship.id}
          showWorkStatus={showWorkStatus}
        />
      ))}
    </Box>
  );
};

export const RelatedWork = (props: {
  workId: string;
  showWorkStatus: boolean;
  relationships?: IRelationships[];
}) => {
  const { workId, relationships, showWorkStatus } = props;

  if (relationships?.length === 0 || !relationships) {
    return <RelatedWorkEmptyView />;
  }

  const groupedByFlow = Object.entries(
    relationships.reduce((acc: Record<string, IRelationships[]>, item) => {
      acc[item.flowName] = [...(acc[item.flowName] || []), item];
      return acc;
    }, {}),
  ).map(([flowName, relationships]) => ({
    flowName,
    relationships,
  }));

  const isMultipleFlows = groupedByFlow.length > 1;

  return (
    <Box display="flex" flexDirection="column" padding={2} gap={2}>
      {groupedByFlow.map(({ flowName, relationships }) => {
        return (
          <RelatedWorkGroup
            workId={workId}
            key={flowName}
            flowName={flowName}
            relationships={relationships}
            isMultipleFlows={isMultipleFlows}
            showWorkStatus={showWorkStatus}
          />
        );
      })}
    </Box>
  );
};
